import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { AlertTriangle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CheckoutMock() {
  const { slug, sp } = useStore();
  const [params] = useSearchParams();
  const session = params.get("session");
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [submitting, setSubmitting] = useState(null); // null | 'success' | 'fail'

  // Fetch tenant for layout chrome (best-effort)
  if (!tenant) {
    api.get(`/public/tenants/${slug}`).then((r) => setTenant(r.data)).catch(() => {});
  }

  if (status === "loading") return null;
  if (status !== "authenticated" || !user) {
    return <Navigate to={sp(`/login`)} replace />;
  }

  const onConfirm = async (outcome) => {
    if (!session) {
      toast.error("Missing session id.");
      return;
    }
    setSubmitting(outcome);
    try {
      const { data } = await api.post("/checkout/mock-confirm", { session_id: session, outcome });
      if (outcome === "success") {
        toast.success("Payment confirmed (mock)");
        navigate(sp(`/account/billing?just_paid=1`), { replace: true });
      } else {
        toast.error("Payment marked failed (mock)");
        // Stay on page; allow retry by going back.
      }
      setSubmitting(null);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(null);
    }
  };

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
      <div className="max-w-xl mx-auto px-6 py-16" data-testid="checkout-mock-page">
        <div className="border-2 border-amber-400/50 bg-amber-500/10 p-5 mb-8 flex items-start gap-3" data-testid="checkout-mock-banner">
          <AlertTriangle className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-amber-200 uppercase tracking-[0.2em] text-sm">MOCK CHECKOUT — no real charge</div>
            <p className="text-sm text-amber-100/70 font-light mt-1">
              This is a placeholder for the real Stripe / Razorpay redirect. Click a button below to simulate the payment outcome.
            </p>
          </div>
        </div>

        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Session</p>
        <h1 className="mt-2 font-display font-bold text-3xl tracking-tight">Confirm your payment</h1>
        <p className="mt-2 text-white/55 text-sm">
          Session id: <span className="font-mono text-white/70 break-all">{session}</span>
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => onConfirm("success")}
            disabled={!!submitting}
            className="px-5 py-5 text-sm uppercase tracking-[0.25em] font-medium bg-emerald-500 hover:bg-emerald-400 text-black inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            data-testid="checkout-mock-success"
          >
            {submitting === "success" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            Simulate success
          </button>
          <button
            onClick={() => onConfirm("fail")}
            disabled={!!submitting}
            className="px-5 py-5 text-sm uppercase tracking-[0.25em] font-medium border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center gap-2 transition-transform disabled:opacity-50"
            data-testid="checkout-mock-fail"
          >
            {submitting === "fail" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-5 h-5" />}
            Simulate failure
          </button>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="mt-6 text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white"
          data-testid="checkout-mock-cancel"
        >
          ← Cancel and go back
        </button>
      </div>
    </StorefrontLayout>
  );
}
